// M4-B view models (pure reads), re-exported through src/sim/selectors.js. All return sensible, JSON-safe objects
// before 2000 (active: false) so the UI can render placeholders.
import { TAKEAWAY_ZONES, lerpAt, wtiRatio, hubSpread, zoneUtilization, NEGATIVE_PRICE, M4_FROM } from '../../data/m4/rules.js';
import { fieldTransports } from '../../data/fields.js';
import { eqDef, fieldDef, stat, eraDollars, isAvailable, yf, yearOf } from '../defs.js';
import { netback, opexPerWell } from '../production.js';
import { liftingCostPerBbl } from '../m3/fiscal.js';
import { m3TransportBlocked } from '../m3/megaprojects.js';
import { spotPrice } from '../../economy/market.js';
import { peek, r2 } from './marketCore.js';
import {
  fieldZoneId, zoneDef, fieldPriceInfo, zonePriceToday, wtiToday, brentToday, endogenousSpread, zoneCurtailment, exportsLegal,
  HUB_NAMES, DELIVERY, isNegativeDay, hubPriceToday,
} from './marketPrices.js';
import { gasMarketViewImpl, esgViewImpl } from './marketGas.js';
import { rigMarketViewImpl } from './marketRigs.js';
import { shaleFinanceViewImpl, quoteHedgeM4 } from './marketFinance.js';
import { negativeDayViewImpl, cushingQuote } from './marketNegative.js';
import { fiscalM4ViewImpl } from './marketFiscal.js';

// Hub price & takeaway card for one field.
export function m4HubCardView(state, fieldId) {
  const fdef = fieldDef(fieldId);
  if (!fdef) return null;
  const y = yf(state);
  const on = y >= M4_FROM;
  const zone = fieldZoneId(fieldId);
  const z = zoneDef(zone);
  const hub = z?.hub ?? 'brent';
  if (!on) return { fieldId, active: false, zone, hub, hubName: HUB_NAMES[hub] ?? hub };
  const info = fieldPriceInfo(state, fieldId);
  const wti = wtiToday(state), brent = brentToday(state);
  const zt = TAKEAWAY_ZONES[zone];
  const f = state.fields[fieldId];
  const options = fieldTransports(fdef, yearOf(state)).map((id) => {
    const d = eqDef(id);
    if (!d || ['natural_gas', 'lng'].includes(d.stats?.product)) return null;
    const p = fieldPriceInfo(state, fieldId, id);
    const cost = eraDollars(state, stat(d, 'costPerBbl', 0), d.baseYear ?? d.year ?? 1859);
    return { defId: id, name: d.name, route: p.kind, dest: p.dest, destName: p.dest ? DELIVERY[p.dest]?.name : null, price: r2(p.price), costPerBbl: r2(cost), net: r2(p.price - cost),
      capacityPerDay: stat(d, 'capacityPerDay', 0), firm: stat(d, 'firm', false), current: f?.transportId === id, connected: f?.connected?.includes(id) ?? false,
      available: isAvailable(d, state) && !m3TransportBlocked(state, fieldId, d) };
  }).filter(Boolean);
  const M = peek(state);
  return {
    fieldId, active: true, zone, zoneName: zone, hub, hubName: HUB_NAMES[hub] ?? hub, brent: r2(brent), wti: r2(wti), wtiBrentRatio: r2(wtiRatio(y)), wtiBrent: r2(wti - brent),
    hubSpreadVsWti: hub === 'brent' ? r2(brent - wti) : r2(hubSpread(hub, y)), zonePrice: r2(info.zonePrice), realized: r2(info.price), route: info.kind, dest: info.dest,
    deliveredPrice: info.deliveredPrice != null ? r2(info.deliveredPrice) : null, exportPrice: info.exportPrice != null ? r2(info.exportPrice) : null,
    exportsLegal: exportsLegal(state), negative: info.negative, negativeDay: isNegativeDay(state),
    takeaway: zt?.capacityKbd ? { capacityKbd: Math.round(lerpAt(zt.capacityKbd, y)), productionKbd: Math.round(lerpAt(zt.productionKbd, y)), utilization: r2(zoneUtilization(zone, y)),
      playerKbd: M?.zoneKbd?.[zone] ?? 0, playerSpread: r2(endogenousSpread(state, zone)), railCost: zt.rail ?? null, truckCost: zt.trucking ?? null } : null,
    curtailment: { order: r2(zoneCurtailment(zone, y)), field: M?.curtail?.[fieldId] ? { ...M.curtail[fieldId] } : null },
    firm: M?.firm?.[fieldId] ? { ...M.firm[fieldId], daysLeft: M.firm[fieldId].untilDay - state.day } : null,
    options,
  };
}

// World/hub price strip for the market panel.
export function m4MarketsView(state) {
  const y = yf(state);
  const on = y >= M4_FROM;
  const hubs = on ? Object.fromEntries(['brent', 'wti_cushing', 'wti_midland', 'bakken', 'wcs', 'lls', 'mars'].map((h) => [h, r2(h === 'brent' ? brentToday(state) : hubPriceToday(state, h))])) : null;
  return { year: y, active: on, spot: r2(spotPrice(state)), hubs, hubNames: HUB_NAMES, exportsLegal: on && exportsLegal(state), negativeDay: isNegativeDay(state), negativeSettle: NEGATIVE_PRICE.settle,
    zones: on ? Object.entries(TAKEAWAY_ZONES).filter(([, z]) => z.capacityKbd).map(([id, z]) => ({ id, hub: z.hub, utilization: r2(zoneUtilization(id, y)), price: r2(zonePriceToday(state, id)) })) : [] };
}

export const m4GasMarketView = (state, fieldId = null) => gasMarketViewImpl(state, fieldId);
export const m4EsgView = (state) => esgViewImpl(state);
export const m4RigMarketView = (state) => rigMarketViewImpl(state);
export const m4ShaleFinanceView = (state) => shaleFinanceViewImpl(state);
export const m4HedgeQuote = (state, opts = {}) => quoteHedgeM4(state, opts);
export const m4NegativeDayView = (state) => negativeDayViewImpl(state);
export const m4CushingQuote = (state, opts = {}) => cushingQuote(state, opts);
export function m4FiscalView(state, fieldId) {
  if (!fieldDef(fieldId)) return null;
  const nb = netback(state, fieldId);
  const cost = liftingCostPerBbl(state, fieldId, opexPerWell(yf(state)), nb);
  return fiscalM4ViewImpl(state, fieldId, nb, cost);
}
